import React, { useEffect, useState } from "react";
import { Table, Card, Row, Col, Spinner, Button } from "react-bootstrap";
import { LMSBASEURL } from "../../config/apiUrls";
import Select from "react-select";
import LoadingWithMessage from "../../components/LoadingWithMessage";
import AnimatedLoader from "../../components/animated-loading";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: "1px solid #ced4da",
    boxShadow: 'none',
    '&:hover': {
      border: "1px solid #ced4da",
    },

    minHeight: "38px",
    fontSize: "14px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6c757d",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#495057",
  }),
}

const StudentDetails = () => {
  const [programsListData, setProgramsListData] = useState([]);
  const [studentsListData, setStudentsListData] = useState([]);
  const [progressData, setProgressData] = useState([]);

  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage] = useState(10);
  const [loadingProgress, setProgressLoading] = useState(false);
  const institutionId = sessionStorage.getItem("instituteId");
  const instituteLoginId = sessionStorage.getItem("id");
  const token = process.env.REACT_APP_API_TOKEN;

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${LMSBASEURL}/api/institutes/${institutionId}/programs`, {
          headers: {
            Authorization: token,
            Accept: "application/json",
          },
        });
        const data = await res.json();
        if (data.status) {
          const formatted = data.data.map((p) => ({
            label: p.program_title,
            value: p.id,
          }));
          setProgramsListData(formatted);
        }
      } catch (err) {
        console.error("Program fetch error:", err);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const programIds = selectedProgram == null? "all": selectedProgram.value;

        const res = await fetch(`${LMSBASEURL}/api/students/by-program`, {
          method: "POST",
          headers: {
            Authorization: token,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            institution_id: institutionId,
            program_ids: programIds,
          }),
        });

        const data = await res.json();

        if (data.status) {
          const formattedStudents = data.data.map((s) => ({
            label: `${s.first_name} ${s.last_name}`,
            value: s.id,
            program_id: s.program_id,
          }));
          setStudentsListData(formattedStudents);
        }
      } catch (err) {
        console.error("Student fetch error:", err);
      }
    };
    fetchStudents();
  }, [selectedProgram]);

  useEffect(() => {
    const fetchProgress = async () => {
      setProgressLoading(true)
      const raw = JSON.stringify({
        "per_page": perPage,
        "page":currentPage,
        "program_id":selectedProgram==null ? "":selectedProgram?.value,
        "student_id":selectedStudent==null ? "":selectedStudent?.value
      });

      try {
        const res = await fetch(
          `${LMSBASEURL}/api/all-student-progress/${institutionId}`,
          {
            method:"POST",
            headers: {
              Authorization: token,
              Accept: "application/json",
              "Content-Type":"application/json"
            },
            body:raw
          }
        );
        const data = await res.json();
        setProgressData(data.data);
        setTotalRecords(data.total_records || 0);
      } catch (err) {
        console.error("Progress fetch error:", err);
      }finally{
        setProgressLoading(false)
      }
    };
    fetchProgress();
  }, [currentPage,selectedProgram, selectedStudent]);

  return (
    <Card className="shadow-sm p-3">
      <h5 className="fw-bold mb-3">Student Progress</h5>

      <Row className="mb-3">
        <Col md={3}>
          <label className="fw-semibold mb-1">Filter by Program:</label>
          
          <Select
            value={selectedProgram}
            onChange={setSelectedProgram}
            options={programsListData}
            placeholder="Select Programs"
            hasError={false}
            styles={customSelectStyles}
            closeMenuOnSelect={true}
            isClearable
            components={{ 
              DropdownIndicator: () => null, 
              IndicatorSeparator: () => null, 
              MultiValue: ()=> null,
            }}
          />
        </Col>
        <Col md={3}>
          <label className="fw-semibold mb-1">Filter by Student:</label>
          <Select
            value={selectedStudent}
            onChange={setSelectedStudent}
            options={studentsListData}
            placeholder="Select Students"
            hasError={false}
            styles={customSelectStyles}
            closeMenuOnSelect={true}
            isClearable
            components={{ 
              DropdownIndicator: () => null, 
              IndicatorSeparator: () => null, 
              MultiValue: ()=> null,
            }}
          />
        </Col>
        <Col md={3}>
        </Col>
        <Col md={3} className="text-end mt-4">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setSelectedProgram(null);
              setSelectedStudent(null);
              setCurrentPage(1);
            }}
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      <hr/>

      {loadingProgress ? (
        <>
        <AnimatedLoader/>
        <LoadingWithMessage loadingProgress={loadingProgress} />
        </>
      ) : (
        <div className="table-responsive">
          <Table bordered hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Student Name</th>
                <th>Program</th>
                <th>Module</th>
                <th>Video (Completed/Total)</th>
                <th>Quizzes (Completed/Total)</th>
              </tr>
            </thead>
            <tbody>
              {progressData?.length > 0 ? (
                progressData?.map((item, idx) => (
                  <tr key={idx} className="text-dark">
                    <td>{item.student_name}</td>
                    <td>{item.program_title}</td>
                    <td>{item.module_title}</td>
                    <td>
                      {item.total_chapter_completed_count}/
                      {item.total_chapter_count}
                    </td>
                    <td>
                      {item.total_quiz_completed_count}/
                      {item.total_quiz_count}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {totalRecords > perPage && (
            <div className="d-flex justify-content-between align-content-between mt-3">
              <div className="mt-2">
                <small className="text-muted">
                  Showing {totalRecords > 0 ? ((currentPage - 1) * perPage) + 1 : 0} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} entries
                </small>
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
    
                  {(() => {
                    const totalPages = Math.ceil(totalRecords / perPage);
                    const pages = [];
                    const maxVisible = 5;
    
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }
    
                    if (startPage > 1) {
                      pages.push(1);
                      if (startPage > 2) pages.push("ellipsis-start");
                    }
    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(i);
                    }
    
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) pages.push("ellipsis-end");
                      pages.push(totalPages);
                    }
    
                    return pages.map((page, idx) => {
                      if (page === "ellipsis-start" || page === "ellipsis-end") {
                        return (
                          <li key={idx} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
    
                      return (
                        <li
                          key={idx}
                          className={`page-item ${page === currentPage ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      );
                    });
                  })()}
    
                  <li
                    className={`page-item ${
                      currentPage >= Math.ceil(totalRecords / perPage)
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        currentPage < Math.ceil(totalRecords / perPage) &&
                        setCurrentPage(currentPage + 1)
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      )}

    </Card>
  );
};

export default StudentDetails